import type { ColDef } from "./types";

export const fmtText = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

function _normalizeArrayItems(values: unknown[]): string[] | null {
  const items = values
    .filter((item) => typeof item === "string" || typeof item === "number")
    .map((item) => String(item).trim())
    .filter((item) => item.length > 0);
  return items.length > 0 ? items : null;
}

export function parseArrayValue(v: unknown): string[] | null {
  if (v === null || v === undefined || v === "") return null;
  if (Array.isArray(v)) return _normalizeArrayItems(v);
  const s = String(v).trim();
  if (!s) return null;
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return _normalizeArrayItems(parsed);
  } catch {}
  // Best-effort: handle Python-style lists (e.g. ['a', 'b']) only when the
  // string looks like a list and contains no apostrophes inside values.
  if (s.startsWith("[") && s.includes("'")) {
    try {
      const parsed = JSON.parse(s.replace(/'/g, '"'));
      if (Array.isArray(parsed)) return _normalizeArrayItems(parsed);
    } catch {}
  }
  return null;
}

// Displays the raw stored value — normalizes single quotes to double quotes only.
export const fmtArray = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return JSON.stringify(v);
  const s = String(v).trim();
  if (!s) return "—";
  if (s.startsWith("[") && s.includes("'")) {
    try {
      const normalized = s.replace(/'/g, '"');
      JSON.parse(normalized);
      return normalized;
    } catch {}
  }
  return s;
};

export const fmtNum = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  return isNaN(n)
    ? String(v)
    : new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
};

export const fmtPct = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  return isNaN(n) ? String(v) : (n * 100).toFixed(2) + "%";
};

export const fmtMult = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  return isNaN(n) ? String(v) : n.toFixed(2) + "x";
};

// Columns grouped by semantic section; order within each group follows mastersheet.
export const ISSUER_COLS: ColDef[] = [
  // ── Identity ─────────────────────────────────────────────────────────────
  { key: "company_name_long",               header: "Company Name",          size: 200, fmt: fmtText,  group: "identity" },
  { key: "k1_gp_id_pc",                    header: "GP ID PC",               size: 90,  fmt: fmtText,  group: "identity" },
  { key: "brand_name",                      header: "PC Name (eFront)",       size: 140, fmt: fmtText,  group: "identity" },
  { key: "website",                         header: "Website",                size: 160, fmt: fmtText,  group: "identity" },
  { key: "legal_form",                      header: "Legal Form",             size: 140, fmt: fmtText,  group: "identity" },
  { key: "country_of_domicile",             header: "Country of Domicile",    size: 140, fmt: fmtText,  group: "identity" },
  { key: "city",                            header: "City",                   size: 110, fmt: fmtText,  group: "identity" },
  { key: "company_with_connection_to_germany", header: "Connection to Germany", size: 100, fmt: fmtText, group: "identity" },
  { key: "lei_code",                        header: "LEI Code",               size: 160, fmt: fmtText,  group: "identity" },
  { key: "commercial_register_number",      header: "Commercial Register Nr.", size: 155, fmt: fmtText, group: "identity" },
  { key: "k1_rl_number",                   header: "RL Number",              size: 110, fmt: fmtText,  group: "identity" },
  { key: "is_listed",                       header: "Listed",                 size: 65,  fmt: fmtText,  group: "identity" },
  { key: "was_renamed",                     header: "Was renamed?",           size: 90,  fmt: fmtText,  group: "identity" },
  { key: "old_names",                       header: "Old Name(s)",            size: 150, fmt: fmtArray, group: "identity", isArray: true },
  // ── Classification ───────────────────────────────────────────────────────
  { key: "sector",                          header: "Sector",                 size: 130, fmt: fmtText,  group: "classification" },
  { key: "vertical",                        header: "Vertical",               size: 130, fmt: fmtText,  group: "classification" },
  { key: "nace_code",                       header: "NACE Code",              size: 100, fmt: fmtText,  group: "classification" },
  { key: "financial_company",               header: "Financial Company",      size: 90,  fmt: fmtText,  group: "classification" },
  { key: "tags",                            header: "Tags",                   size: 200, fmt: fmtArray, group: "classification", isArray: true },
  { key: "drs",                             header: "DRS",                    size: 60,  fmt: fmtText,  group: "classification" },
  // ── Tracking ─────────────────────────────────────────────────────────────
  { key: "pc_new_in_total_portfolio",       header: "New in Total Portfolio", size: 105, fmt: fmtText,  group: "tracking" },
  { key: "seed_pool_raw",                   header: "Seedpool / Fund Inv.",   size: 180, fmt: fmtText,  group: "tracking" },
  { key: "seed_pool",                       header: "Seed Pool",              size: 130, fmt: fmtText,  group: "tracking" },
  { key: "look_through",                    header: "Look-through",           size: 90,  fmt: fmtText,  group: "tracking" },
  { key: "change_indicator_1",              header: "Change Indicator 1",     size: 120, fmt: fmtText,  group: "tracking" },
  { key: "change_indicator_2",              header: "Change Indicator 2",     size: 180, fmt: fmtText,  group: "tracking" },
  { key: "comment",                         header: "Comment",                size: 180, fmt: fmtText,  group: "tracking", lastIssuer: true },
  // ── Description (pinned at end) ──────────────────────────────────────────
  { key: "pc_description",                  header: "Description",            size: 200, fmt: fmtText,  group: "description" },
];

export const POSITION_COLS: ColDef[] = [
  // ── Context ──────────────────────────────────────────────────────────────
  { key: "reporting_date",                  header: "Reporting Date",              size: 115, fmt: fmtText, group: "context" },
  { key: "reporting_currency_fund",         header: "Fund Ccy",                    size: 75,  fmt: fmtText, group: "context" },
  { key: "asset_type",                      header: "Asset Type",                  size: 140, fmt: fmtText, group: "context" },
  { key: "pc_new_in_fund_portfolio",        header: "New in Fund Portfolio",       size: 105, fmt: fmtText, group: "context" },
  { key: "date_pc_new_in_k1_fund_portfolio",header: "Date PC new in Fund Portfolio",size: 160,fmt: fmtText, group: "context" },
  { key: "investment_currency",             header: "Inv. Ccy",                    size: 75,  fmt: fmtText, group: "context" },
  // ── Financials ───────────────────────────────────────────────────────────
  { key: "total_investment_cost",           header: "Total Cost",                  size: 130, fmt: fmtNum, align: "right", group: "financials" },
  { key: "current_costs",                   header: "Active Cost",                 size: 120, fmt: fmtNum, align: "right", group: "financials" },
  { key: "proceeds",                        header: "Proceeds",                    size: 110, fmt: fmtNum, align: "right", group: "financials" },
  { key: "realized_cost",                   header: "Realized Cost",               size: 115, fmt: fmtNum, align: "right", group: "financials" },
  { key: "dividends_income",                header: "Dividends",                   size: 110, fmt: fmtNum, align: "right", group: "financials" },
  { key: "fair_value",                      header: "Fair Value",                  size: 120, fmt: fmtNum, align: "right", group: "financials" },
  { key: "stake_non_diluted",               header: "Stake %",                     size: 80,  fmt: fmtPct, align: "right", group: "financials" },
  { key: "stake_fully_diluted",             header: "Stake % (FD)",                size: 90,  fmt: fmtPct, align: "right", group: "financials" },
  { key: "voting_rights",                   header: "Voting Rights",               size: 90,  fmt: fmtPct, align: "right", group: "financials" },
  { key: "turnover",                        header: "Turnover",                    size: 110, fmt: fmtNum, align: "right", group: "financials" },
  { key: "irr",                             header: "IRR",                         size: 75,  fmt: fmtPct, align: "right", group: "financials" },
  { key: "moic",                            header: "MOIC",                        size: 70,  fmt: fmtMult, align: "right", group: "financials" },
  // ── Status & Lifecycle ───────────────────────────────────────────────────
  { key: "company_investment_state",        header: "Investment State",            size: 120, fmt: fmtText, group: "status" },
  { key: "first_investment_date",           header: "First Inv. Date",             size: 125, fmt: fmtText, group: "status" },
  { key: "date_of_partial_exit",            header: "Partial Exit Date",           size: 125, fmt: fmtText, group: "status" },
  { key: "exit_date",                       header: "Exit Date",                   size: 100, fmt: fmtText, group: "status" },
  { key: "exit_method",                     header: "Exit Method",                 size: 120, fmt: fmtText, group: "status" },
  { key: "stage_at_initial_investment",     header: "Stage (initial)",             size: 130, fmt: fmtText, group: "status" },
  { key: "last_investment_stage",           header: "Stage (last)",                size: 120, fmt: fmtText, group: "status" },
  { key: "k1_unique_identifier",            header: "Unique ID",                   size: 120, fmt: fmtText, group: "status" },
];

export const FUND_COLS: ColDef[] = [
  { key: "k1_gp_id_fonds", header: "Fund GP ID", size: 120, fmt: fmtText },
  { key: "fund_name", header: "Fund Name", size: 180, fmt: fmtText },
  { key: "manager", header: "Manager", size: 150, fmt: fmtText },
  { key: "fund_currency", header: "Fund Currency", size: 120, fmt: fmtText },
  { key: "fund_domicile", header: "Domicile", size: 120, fmt: fmtText },
  { key: "vintage_year", header: "Vintage Year", size: 120, fmt: fmtText },
  { key: "legal_form_fund", header: "Legal Form", size: 140, fmt: fmtText },
  {
    key: "fund_investment_state",
    header: "Investment State",
    size: 130,
    fmt: fmtText,
  },
  { key: "generation", header: "Generation", size: 110, fmt: fmtText },
  { key: "sector_focus", header: "Sector Focus", size: 130, fmt: fmtText },
  { key: "strategy_focus", header: "Strategy Focus", size: 130, fmt: fmtText },
  {
    key: "geographic_focus",
    header: "Geographic Focus",
    size: 140,
    fmt: fmtText,
  },
  { key: "asset_type_fund", header: "Asset Type", size: 120, fmt: fmtText },
  { key: "product_type", header: "Product Type", size: 120, fmt: fmtText },
  { key: "lei_code_fund", header: "LEI Code", size: 150, fmt: fmtText },
  {
    key: "in_which_closing_joined",
    header: "Closing Joined",
    size: 130,
    fmt: fmtText,
  },
  {
    key: "pct_of_private_investors",
    header: "% Private Investors",
    size: 140,
    fmt: fmtPct,
    align: "right",
  },
  {
    key: "si_nav_before_carry_tf",
    header: "SI NAV before carry (FUND)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "si_nav_before_carry_k1",
    header: "SI NAV before carry (K1)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "si_nav_after_carry_tf",
    header: "SI NAV after carry (FUND)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "si_nav_after_carry_k1",
    header: "SI NAV after carry (K1)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "cq_nav_before_carry_tf",
    header: "CQ NAV before carry (FUND)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "cq_nav_before_carry_k1",
    header: "CQ NAV before carry (K1)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "cq_nav_after_carry_tf",
    header: "CQ NAV after carry (FUND)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "cq_nav_after_carry_k1",
    header: "CQ NAV after carry (K1)",
    size: 160,
    fmt: fmtNum,
    align: "right",
  },
];
